import type {
  AncestralId,
  BuildingId,
  GameState,
  JobId,
  ResourceBundle,
  TechId,
} from './types';
import {
  ANCESTRAL_BY_ID,
  BALANCE,
  BUILDINGS_BY_ID,
  JOBS_BY_ID,
  TECHS_BY_ID,
} from './content';
import { bulkCost, maxAffordable, scaledCost } from './numbers';
import { collectModifiers } from './modifiers';
import { computeProduction } from './production';
import { availableWorkers } from './state';

// ---------------------------------------------------------------------------
// Job assignment
// ---------------------------------------------------------------------------

/** Move `delta` workers into (positive) or out of (negative) a job. */
export function assignWorkers(state: GameState, jobId: JobId, delta: number): number {
  const job = JOBS_BY_ID[jobId];
  if (!job) return 0;

  const current = state.jobs[jobId] ?? 0;
  const limit = delta > 0 ? Math.min(delta, availableWorkers(state)) : Math.max(delta, -current);
  if (limit === 0) return 0;

  state.jobs[jobId] = current + limit;
  return limit;
}

/** Set a job to an exact worker count, clamped by the available tribe. */
export function setJobWorkers(state: GameState, jobId: JobId, count: number): void {
  const current = state.jobs[jobId] ?? 0;
  assignWorkers(state, jobId, Math.max(0, Math.floor(count)) - current);
}

/** Return every assigned worker to the idle pool. */
export function clearAssignments(state: GameState): void {
  state.jobs = {};
}

// ---------------------------------------------------------------------------
// Costs
// ---------------------------------------------------------------------------

export function buildingCost(
  state: GameState,
  buildingId: BuildingId,
  quantity = 1,
): ResourceBundle {
  const building = BUILDINGS_BY_ID[buildingId];
  if (!building) return {};
  const owned = state.buildings[buildingId] ?? 0;

  const cost: ResourceBundle = {};
  for (const [resourceId, baseAmount] of Object.entries(building.baseCost)) {
    cost[resourceId] =
      quantity === 1
        ? scaledCost(baseAmount ?? 0, building.costGrowth, owned)
        : bulkCost(baseAmount ?? 0, building.costGrowth, owned, quantity);
  }
  return cost;
}

export function ancestralCost(state: GameState, nodeId: AncestralId): number {
  const node = ANCESTRAL_BY_ID[nodeId];
  if (!node) return Infinity;
  const level = state.ancestral[nodeId] ?? 0;
  return Math.ceil(scaledCost(node.baseCost, node.costGrowth, level));
}

export function canAfford(state: GameState, cost: ResourceBundle): boolean {
  for (const [resourceId, amount] of Object.entries(cost)) {
    if ((state.resources[resourceId] ?? 0) < (amount ?? 0) - 1e-6) return false;
  }
  return true;
}

function spend(state: GameState, cost: ResourceBundle): void {
  for (const [resourceId, amount] of Object.entries(cost)) {
    state.resources[resourceId] = Math.max(0, (state.resources[resourceId] ?? 0) - (amount ?? 0));
  }
}

/** Largest number of a building the tribe can currently pay for, up to `limit`. */
export function affordableBuildingCount(
  state: GameState,
  buildingId: BuildingId,
  limit = 1000,
): number {
  const building = BUILDINGS_BY_ID[buildingId];
  if (!building) return 0;
  const owned = state.buildings[buildingId] ?? 0;

  let best = limit;
  if (building.maxOwned !== undefined) best = Math.min(best, building.maxOwned - owned);

  for (const [resourceId, baseAmount] of Object.entries(building.baseCost)) {
    const budget = state.resources[resourceId] ?? 0;
    best = Math.min(
      best,
      maxAffordable(baseAmount ?? 0, building.costGrowth, owned, budget),
    );
  }
  return Math.max(0, best);
}

// ---------------------------------------------------------------------------
// Purchases
// ---------------------------------------------------------------------------

export function isBuildingUnlocked(state: GameState, buildingId: BuildingId): boolean {
  const building = BUILDINGS_BY_ID[buildingId];
  if (!building) return false;
  if (building.age > state.ageIndex) return false;
  if (building.requiresTech && !state.techs.includes(building.requiresTech)) return false;
  return true;
}

export function buyBuilding(
  state: GameState,
  buildingId: BuildingId,
  quantity = 1,
): number {
  const building = BUILDINGS_BY_ID[buildingId];
  if (!building || !isBuildingUnlocked(state, buildingId)) return 0;

  const owned = state.buildings[buildingId] ?? 0;
  let wanted = Math.max(0, Math.floor(quantity));
  if (building.maxOwned !== undefined) wanted = Math.min(wanted, building.maxOwned - owned);
  if (wanted <= 0) return 0;

  const cost = buildingCost(state, buildingId, wanted);
  if (!canAfford(state, cost)) return 0;

  spend(state, cost);
  state.buildings[buildingId] = owned + wanted;
  return wanted;
}

export function isTechAvailable(state: GameState, techId: TechId): boolean {
  const tech = TECHS_BY_ID[techId];
  if (!tech) return false;
  if (tech.age > state.ageIndex) return false;
  if (state.techs.includes(techId)) return false;
  return tech.requires.every((requirement) => state.techs.includes(requirement));
}

export function buyTech(state: GameState, techId: TechId): boolean {
  const tech = TECHS_BY_ID[techId];
  if (!tech || !isTechAvailable(state, techId)) return false;
  if (!canAfford(state, tech.cost)) return false;

  spend(state, tech.cost);
  state.techs.push(techId);
  return true;
}

export function isAncestralAvailable(state: GameState, nodeId: AncestralId): boolean {
  const node = ANCESTRAL_BY_ID[nodeId];
  if (!node) return false;
  if (node.minAge > state.ageIndex) return false;
  if ((state.ancestral[nodeId] ?? 0) >= node.maxLevel) return false;
  return (node.requires ?? []).every((requirement) => (state.ancestral[requirement] ?? 0) > 0);
}

export function buyAncestral(state: GameState, nodeId: AncestralId): boolean {
  if (!isAncestralAvailable(state, nodeId)) return false;
  const cost = ancestralCost(state, nodeId);
  if (state.ancestralKnowledge < cost) return false;

  state.ancestralKnowledge -= cost;
  state.ancestral[nodeId] = (state.ancestral[nodeId] ?? 0) + 1;
  return true;
}

// ---------------------------------------------------------------------------
// Active play
// ---------------------------------------------------------------------------

export interface ManualGatherResult {
  gained: ResourceBundle;
  vigorStacks: number;
}

/**
 * Work alongside the tribe. Grants a few seconds of current production and
 * adds a Vigor stack, which raises all production while the player keeps going.
 */
export function manualGather(state: GameState): ManualGatherResult {
  const modifiers = collectModifiers(state);
  const production = computeProduction(state, modifiers, 1);
  const gained: ResourceBundle = {};

  let producedAnything = false;
  for (const [resourceId, rate] of Object.entries(production.production)) {
    if (rate <= 0) continue;
    producedAnything = true;
    const cap = production.caps[resourceId] ?? Infinity;
    const amount = rate * BALANCE.manualGatherSeconds;
    const before = state.resources[resourceId] ?? 0;
    state.resources[resourceId] = Math.min(cap, before + amount);
    gained[resourceId] = state.resources[resourceId] - before;
  }

  if (!producedAnything) {
    // Nobody is working yet — bare hands still find something.
    const cap = production.caps.forage ?? Infinity;
    const before = state.resources.forage ?? 0;
    state.resources.forage = Math.min(cap, before + BALANCE.manualGatherFloor);
    gained.forage = state.resources.forage - before;
  }

  const maxStacks = Math.floor(modifiers.vigorMaxStacks);
  state.vigorStacks = Math.min(maxStacks, state.vigorStacks + 1);
  state.vigorDecay = BALANCE.vigorDecaySeconds;
  state.stats.totalManualGathers += 1;

  return { gained, vigorStacks: state.vigorStacks };
}
